import { useEffect, useState } from 'preact/hooks';
import type { ToggleWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import './ToggleRenderer.css';

interface Props {
  widget: ToggleWidget;
  source: IPointBindingSource;
}

/**
 * On/Off toggle with feedback-driven visual state. Concept §1 rule: the
 * toggle position mirrors what the CONTROLLER says, not what the operator
 * just clicked. A pending write shows an overlay ring; when the readback
 * confirms, the ring drops. If the controller rejected the write (readback
 * shows the old value), the ring turns red and stays until the next click
 * or a manual dismiss.
 */
export function ToggleRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.feedbackPoint);
  useEffect(() => release, [release]);

  const [pending, setPending] = useState<{ target: unknown; state: 'sending' | 'rejected' } | null>(null);

  const current = value.value;
  const stale = isDegraded(current);
  const on = coerceOn(current?.value, widget.onValue);

  const canWrite = !pending || pending.state === 'rejected';

  async function commit(target: unknown) {
    if (!canWrite) return;
    setPending({ target, state: 'sending' });
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.commandPoint, target, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPending(null);
          return;
        }
        if (ack.state === 'rejected') {
          setPending({ target, state: 'rejected' });
          return;
        }
      }
      setPending(null);
    } catch {
      setPending(null);
    }
  }

  const onLabel = widget.onLabel ?? 'An';
  const offLabel = widget.offLabel ?? 'Aus';

  return (
    <div class={`hv-toggle${stale ? ' hv-toggle--stale' : ''}${pending ? ` hv-toggle--${pending.state}` : ''}`}>
      {widget.label && <div class="hv-toggle__label">{widget.label}</div>}
      <div class="hv-toggle__row" role="group">
        <button
          type="button"
          class={`hv-toggle__side${on === false ? ' hv-toggle__side--active' : ''}`}
          onClick={() => commit(widget.offValue ?? false)}
          disabled={!canWrite}
          aria-pressed={on === false}
        >
          {offLabel}
        </button>
        <button
          type="button"
          class={`hv-toggle__side${on === true ? ' hv-toggle__side--active' : ''}`}
          onClick={() => commit(widget.onValue ?? true)}
          disabled={!canWrite}
          aria-pressed={on === true}
        >
          {onLabel}
        </button>
      </div>
      {pending && (
        <div class="hv-toggle__pending" role="status">
          {pending.state === 'sending' && (
            <span>… gesendet, warte auf Regler</span>
          )}
          {pending.state === 'rejected' && (
            <span>Regler hat den Wert verworfen</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Interpret the current feedback value as on/off. `widget.onValue` (when
 * supplied) wins so the same widget can drive both a plain boolean and an
 * enum-shaped register (e.g. `1` = "run", `0` = "stop"). Falls back to
 * JS truthy/falsy for a bare bool binding.
 */
function coerceOn(value: unknown, onValue: unknown): boolean | null {
  if (value === null || value === undefined) return null;
  if (onValue !== undefined && onValue !== null) {
    return valuesEqual(value, onValue);
  }
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === 'on' || s === '1') return true;
    if (s === 'false' || s === 'off' || s === '0') return false;
  }
  return null;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a) === String(b);
}
